import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import './PeptideComparison.css';

const PeptideComparison = () => {
  const peptideData = [
    {
      name: 'Semaglutide',
      brand: 'Ozempic/Wegovy',
      mechanism: 'GLP-1 Agonist',
      weightLoss: '15-17%',
      startingDose: '0.25mg/week',
      maxDose: '2.4mg/week',
      fdaApproved: 'Yes',
      halfLife: '7 days',
      sideEffects: 'Nausea, Diarrhea',
      calculator: '/semaglutide-calculator'
    },
    {
      name: 'Tirzepatide',
      brand: 'Mounjaro/Zepbound',
      mechanism: 'GLP-1/GIP Dual Agonist',
      weightLoss: '20-22.5%',
      startingDose: '2.5mg/week',
      maxDose: '15mg/week',
      fdaApproved: 'Yes',
      halfLife: '5 days',
      sideEffects: 'Nausea, Vomiting',
      calculator: '/tirzepatide-calculator'
    },
    {
      name: 'Retatrutide',
      brand: 'Experimental',
      mechanism: 'GLP-1/GIP/Glucagon Triple Agonist',
      weightLoss: '24-25%',
      startingDose: '1mg/week',
      maxDose: '12mg/week',
      fdaApproved: 'Phase 3 Trials',
      halfLife: '5-7 days',
      sideEffects: 'Nausea, Fatigue',
      calculator: '/retatrutide-calculator'
    },
    {
      name: 'BPC-157',
      brand: 'Research Peptide',
      mechanism: 'Gastric Pentadecapeptide',
      weightLoss: 'Healing/Recovery',
      startingDose: '200-400mcg/day',
      maxDose: '800mcg/day',
      fdaApproved: 'Research Only',
      halfLife: '4 hours',
      sideEffects: 'Minimal',
      calculator: '/bpc-157-calculator'
    },
    {
      name: 'CJC-1295',
      brand: 'Growth Hormone Analog',
      mechanism: 'GHRH Analog',
      weightLoss: 'Muscle Growth',
      startingDose: '1mg/week',
      maxDose: '2mg/week',
      fdaApproved: 'Research Only',
      halfLife: '6-8 days',
      sideEffects: 'Injection Site Reactions',
      calculator: '/cjc-1295-calculator'
    },
    {
      name: 'Ipamorelin',
      brand: 'Growth Hormone Secretagogue',
      mechanism: 'GHRP Analog',
      weightLoss: 'Body Composition',
      startingDose: '200-300mcg/day',
      maxDose: '600mcg/day',
      fdaApproved: 'Research Only',
      halfLife: '2 hours',
      sideEffects: 'Minimal',
      calculator: '/ipamorelin-calculator'
    }
  ];

  return (
    <div className="peptide-compare-page">
      <SEOHead 
        title="Peptide Comparison Table - Compare Semaglutide, Tirzepatide, Retatrutide & More | PeptideDose"
        description="Compare peptides side-by-side: Semaglutide vs Tirzepatide vs Retatrutide. Weight loss efficacy, dosing, side effects, and FDA approval status for all major peptides."
      />

      <div className="peptide-compare-hero">
        <div className="peptide-compare-hero-content">
          <Link to="/" className="peptide-compare-back-link">← Back to Main Calculator</Link>
          <h1 className="peptide-compare-title">Peptide Comparison Table</h1>
          <p className="peptide-compare-subtitle">
            Compare efficacy, dosing, and safety profiles of major weight loss and therapeutic peptides
          </p>
        </div>
      </div>

      <div className="peptide-compare-container">
        <div className="peptide-compare-table-wrapper">
          <table className="peptide-compare-table">
            <thead>
              <tr className="peptide-compare-header-row">
                <th className="peptide-compare-header">Peptide</th>
                <th className="peptide-compare-header">Brand Name</th>
                <th className="peptide-compare-header">Mechanism</th>
                <th className="peptide-compare-header">Weight Loss</th>
                <th className="peptide-compare-header">Starting Dose</th>
                <th className="peptide-compare-header">Max Dose</th>
                <th className="peptide-compare-header">FDA Status</th>
                <th className="peptide-compare-header">Half-Life</th>
                <th className="peptide-compare-header">Main Side Effects</th>
                <th className="peptide-compare-header">Calculator</th>
              </tr>
            </thead>
            <tbody>
              {peptideData.map((peptide, index) => (
                <tr key={index} className="peptide-compare-row">
                  <td className="peptide-compare-cell peptide-compare-name">{peptide.name}</td>
                  <td className="peptide-compare-cell">{peptide.brand}</td>
                  <td className="peptide-compare-cell">{peptide.mechanism}</td>
                  <td className="peptide-compare-cell peptide-compare-weight-loss">{peptide.weightLoss}</td>
                  <td className="peptide-compare-cell">{peptide.startingDose}</td>
                  <td className="peptide-compare-cell">{peptide.maxDose}</td>
                  <td className={`peptide-compare-cell ${peptide.fdaApproved === 'Yes' ? 'peptide-compare-approved' : 'peptide-compare-research'}`}>
                    {peptide.fdaApproved}
                  </td>
                  <td className="peptide-compare-cell">{peptide.halfLife}</td>
                  <td className="peptide-compare-cell">{peptide.sideEffects}</td>
                  <td className="peptide-compare-cell">
                    <Link to={peptide.calculator} className="peptide-compare-calc-link">
                      Calculate
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="peptide-compare-info-section">
          <div className="peptide-compare-info-grid">
            <div className="peptide-compare-info-card">
              <h3 className="peptide-compare-info-title">🏆 Weight Loss Champions</h3>
              <p className="peptide-compare-info-text">
                Retatrutide leads with 24-25% weight loss in trials, followed by Tirzepatide (22.5%) and Semaglutide (15-17%). 
                All three show superior results to traditional weight loss methods.
              </p>
            </div>

            <div className="peptide-compare-info-card">
              <h3 className="peptide-compare-info-title">🔬 Mechanism Differences</h3>
              <p className="peptide-compare-info-text">
                Single agonists (Semaglutide) target GLP-1 only. Dual agonists (Tirzepatide) add GIP. 
                Triple agonists (Retatrutide) include Glucagon for enhanced metabolic effects.
              </p>
            </div>

            <div className="peptide-compare-info-card">
              <h3 className="peptide-compare-info-title">⚕️ FDA Approval Status</h3>
              <p className="peptide-compare-info-text">
                Semaglutide and Tirzepatide are FDA approved for diabetes and weight loss. 
                Retatrutide is in Phase 3 trials. BPC-157, CJC-1295, and Ipamorelin are research-only.
              </p>
            </div>

            <div className="peptide-compare-info-card">
              <h3 className="peptide-compare-info-title">🎯 Choosing the Right Peptide</h3>
              <p className="peptide-compare-info-text">
                For weight loss: Tirzepatide or Semaglutide (FDA approved). For healing: BPC-157. 
                For muscle growth: CJC-1295 + Ipamorelin stack. Always consult healthcare providers.
              </p>
            </div>
          </div>
        </div>

        <div className="peptide-compare-cta-section">
          <h2 className="peptide-compare-cta-title">Ready to Get Started?</h2>
          <p className="peptide-compare-cta-text">
            Use our calculators to determine exact dosing and reconstitution for your chosen peptide.
          </p>
          <div className="peptide-compare-cta-buttons">
            <Link to="/" className="peptide-compare-cta-button peptide-compare-primary">
              Main Calculator
            </Link>
            <Link to="/tools/dosage-converter" className="peptide-compare-cta-button peptide-compare-secondary">
              Dosage Converter
            </Link>
          </div>
        </div>
      </div>

      <footer className="peptide-compare-footer">
        <div className="peptide-compare-footer-content">
          <div className="peptide-compare-footer-links">
            <Link to="/" className="peptide-compare-footer-link">Main Calculator</Link>
            <Link to="/faq" className="peptide-compare-footer-link">FAQ</Link>
            <Link to="/guides" className="peptide-compare-footer-link">Guides</Link>
          </div>
          <p className="peptide-compare-footer-disclaimer">
            ⚠️ Always consult with a healthcare professional before using any peptide
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PeptideComparison;